import { useEffect, useState } from "react";

import { Input } from "@/components/ui/input";
import { APP_LOCALE } from "../misc/locale";
import { assertValidAmount, assertValidBudgetRange } from "./profileRules";

const NO_DATA_LABEL = "Sin dato";

/** MXN, no decimals, tabular-friendly: "$2,500,000". */
export function formatMXN(value: number): string {
  return new Intl.NumberFormat(APP_LOCALE, {
    style: "currency",
    currency: "MXN",
    currencyDisplay: "narrowSymbol",
    maximumFractionDigits: 0,
  }).format(value);
}

/** Strips everything but digits and a decimal point; empty input is `null`. */
function parseAmountInput(raw: string): number | null {
  const cleaned = raw.replace(/[^0-9.]/g, "");
  if (cleaned === "") return null;
  const parsed = Number(cleaned);
  return Number.isNaN(parsed) ? null : parsed;
}

function toErrorMessage(validate: () => void): string | null {
  try {
    validate();
    return null;
  } catch (err) {
    return err instanceof Error ? err.message : null;
  }
}

interface AmountInputProps {
  id: string;
  label: string;
  value: number | null;
  readOnly: boolean;
  validate: (parsed: number | null) => string | null;
  onCommit: (parsed: number | null) => Promise<void>;
}

/**
 * A single MXN amount field: typed as digits (thousands separators
 * accepted), saved on blur or Enter only when changed and valid, formatted
 * as currency once the field loses focus. Reverts to the last saved value on
 * a failed save.
 */
function AmountInput({
  id,
  label,
  value,
  readOnly,
  validate,
  onCommit,
}: AmountInputProps) {
  const [raw, setRaw] = useState(value != null ? String(value) : "");
  const [isFocused, setIsFocused] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isFocused) {
      setRaw(value != null ? String(value) : "");
    }
  }, [value, isFocused]);

  const displayValue = isFocused ? raw : value != null ? formatMXN(value) : "";

  async function commit() {
    const parsed = parseAmountInput(raw);
    if (parsed === value) return;

    const validationError = validate(parsed);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError(null);
    try {
      await onCommit(parsed);
    } catch {
      setRaw(value != null ? String(value) : "");
    }
  }

  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-sm font-medium">
        {label}
      </label>
      {readOnly ? (
        <span data-testid={id} className="text-[15px] tabular-nums">
          {value != null ? formatMXN(value) : NO_DATA_LABEL}
        </span>
      ) : (
        <Input
          id={id}
          data-testid={id}
          inputMode="decimal"
          className="min-h-11 text-base tabular-nums md:text-[15px]"
          value={displayValue}
          onFocus={() => setIsFocused(true)}
          onChange={(event) => setRaw(event.target.value)}
          onBlur={() => {
            setIsFocused(false);
            void commit();
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.currentTarget.blur();
            }
          }}
        />
      )}
      {error ? (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}

export interface ProfileBudgetFieldsProps {
  budgetMin: number | null;
  budgetMax: number | null;
  onSaveMin: (value: number | null) => Promise<void>;
  onSaveMax: (value: number | null) => Promise<void>;
  readOnly: boolean;
  minLabel: string;
  maxLabel: string;
  className?: string;
}

/**
 * Almikia Epic EF (TASK-119), DATA CONTRACT EF (2)/(6): "Presupuesto desde /
 * hasta" -- validated together (TASK-117's `assertValidBudgetRange`) even
 * though each bound saves independently on its own blur.
 */
export function ProfileBudgetFields({
  budgetMin,
  budgetMax,
  onSaveMin,
  onSaveMax,
  readOnly,
  minLabel,
  maxLabel,
  className,
}: ProfileBudgetFieldsProps) {
  return (
    <div className={className}>
      <AmountInput
        id="profile-budget-min"
        label={minLabel}
        value={budgetMin}
        readOnly={readOnly}
        validate={(parsed) =>
          toErrorMessage(() => assertValidBudgetRange(parsed, budgetMax))
        }
        onCommit={onSaveMin}
      />
      <AmountInput
        id="profile-budget-max"
        label={maxLabel}
        value={budgetMax}
        readOnly={readOnly}
        validate={(parsed) =>
          toErrorMessage(() => assertValidBudgetRange(budgetMin, parsed))
        }
        onCommit={onSaveMax}
      />
    </div>
  );
}

export interface ProfileSingleAmountFieldProps {
  id: string;
  label: string;
  value: number | null;
  readOnly: boolean;
  onSave: (value: number | null) => Promise<void>;
}

/** A standalone MXN amount field (Enganche, Mensualidad). */
export function ProfileSingleAmountField({
  id,
  label,
  value,
  readOnly,
  onSave,
}: ProfileSingleAmountFieldProps) {
  return (
    <AmountInput
      id={id}
      label={label}
      value={value}
      readOnly={readOnly}
      validate={(parsed) => toErrorMessage(() => assertValidAmount(parsed))}
      onCommit={onSave}
    />
  );
}
